import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Loader2, Crown, Trophy, ShieldCheck, Sword, Star } from 'lucide-react';
import api from '../../lib/api';
import { RARITY_UI } from '../../lib/rarity';
import RarityBadge from '../../components/RarityBadge';

const RANK_COLORS = {
  Bronze: '#B08B58', Silver: '#C0C0C0', Gold: '#E9C46A', Platinum: '#8ECAE6', Diamond: '#FF6EC7',
};

export default function BattleProfile() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Deck data removed in iter74 — tap combat is card-only now.
    Promise.all([
      api.get('/battle-stats/me').then(r => r.data),
      api.get('/battles/mine?limit=10').then(r => r.data).catch(() => []),
    ]).then(([s, h]) => {
      setStats(s); setRecent(h || []);
      setLoading(false);
    });
  }, []);

  if (loading) return <div className="p-8 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-muted-foreground" /></div>;

  const rankColor = RANK_COLORS[stats?.rank?.tier] || '#E9C46A';
  const fav = stats?.favorite_card;
  const favUI = fav ? (RARITY_UI[fav.rarity] || RARITY_UI.common) : null;

  return (
    <div className="space-y-4" data-testid="battle-profile-root">
      <button onClick={() => nav('/battle')} data-testid="battle-profile-back" className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><ChevronLeft className="w-3 h-3" /> Back to Arena</button>
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-accent">Battle</p>
        <h2 className="font-serif text-3xl">Combatant Profile</h2>
      </div>

      <div className="rounded-3xl p-5 relative overflow-hidden border border-white/10"
        style={{ background: `linear-gradient(135deg, ${rankColor}22, transparent 60%)` }} data-testid="battle-profile-hero">
        <Crown className="absolute -right-6 -top-6 w-32 h-32 opacity-10" style={{ color: rankColor }} />
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Battle Rating</p>
        <p className="font-mono text-5xl tabular-nums mt-1" style={{ color: rankColor }} data-testid="battle-profile-rating">{stats?.rating ?? 1000}</p>
        <p className="text-sm mt-1" style={{ color: rankColor }}>{stats?.rank?.label || 'Unranked'}</p>
      </div>

      <div className="grid grid-cols-4 gap-2">
        <MiniStat label="Wins" value={stats?.wins ?? 0} accent="#4ade80" testid="battle-profile-wins" />
        <MiniStat label="Losses" value={stats?.losses ?? 0} accent="#f87171" testid="battle-profile-losses" />
        <MiniStat label="Win rate" value={`${stats?.win_rate ?? 0}%`} testid="battle-profile-winrate" />
        <MiniStat label="Total" value={stats?.total_battles ?? 0} testid="battle-profile-total" />
      </div>

      {fav && (
        <section>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Favorite card</p>
          <div className={`rounded-2xl p-3 border ${favUI.borderClass}`} style={favUI.borderStyle} data-testid="battle-profile-fav-card">
            <div className={`rounded-2xl p-3 ${favUI.cardClass}`} style={favUI.cardStyle}>
              <div className="flex items-center gap-2">
                <RarityBadge rarity={fav.rarity} size="sm" showParticles={false} />
                <p className="font-serif text-base truncate">{fav.species_common_name}</p>
              </div>
              <div className="mt-1 flex items-center gap-3 text-[10px] text-muted-foreground">
                <span>Rating <span className="font-mono" style={{ color: favUI.color }}>{fav.battle_rating}</span></span>
                <span>Wins with this card <span className="font-mono text-emerald-400">{fav.wins_with_card}</span></span>
              </div>
            </div>
          </div>
        </section>
      )}

      <section>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Recent matches</p>
          <button onClick={() => nav('/battle/history')} data-testid="battle-profile-view-history" className="text-[10px] uppercase tracking-widest text-accent">View all</button>
        </div>
        {recent.length === 0 ? (
          <div className="rounded-2xl bg-card border border-white/5 p-4 text-center text-xs text-muted-foreground">No matches yet.</div>
        ) : (
          <div className="rounded-2xl bg-card border border-white/5 overflow-hidden">
            {recent.slice(0, 5).map(b => (
              <div key={b.battle_id} className="flex items-center gap-2 p-3 border-b border-white/5 last:border-0" data-testid="battle-profile-history-row">
                <div className={`w-1 h-8 rounded-full ${b.winner === 'challenger' ? 'bg-emerald-400' : 'bg-red-400'}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-xs truncate">{b.challenger_snapshot?.name} vs {b.opponent_snapshot?.name}</p>
                  <p className="text-[10px] text-muted-foreground">{(b.mode || 'practice').toUpperCase()} · {new Date(b.created_at).toLocaleDateString()}</p>
                </div>
                <span className={`font-mono text-[10px] ${b.winner === 'challenger' ? 'text-emerald-400' : 'text-red-400'}`}>
                  {b.round_wins.challenger}–{b.round_wins.opponent}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function MiniStat({ label, value, accent, testid }) {
  return (
    <div className="rounded-2xl bg-card border border-white/5 p-3 text-center" data-testid={testid}>
      <p className="font-mono font-semibold text-lg tabular-nums" style={{ color: accent }}>{value}</p>
      <p className="text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}
